import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { KeyRound, Loader2 } from 'lucide-react';
import { UserProfile } from '../../lib/api';

interface SetTempPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The user a temp password is being set for (display only). */
  targetUser: UserProfile | null;
  actionLoading: boolean;
  /** Generate + set the temp password; forceChange opts into a forced reset. */
  onConfirm: (forceChange: boolean) => Promise<void>;
}

/**
 * Confirm dialog for setting a user's temporary password.
 *
 * The forced password change is opt-in (checkbox, default OFF): setting a temp
 * password just hands the admin a working credential to relay. Only when the
 * admin ticks the box is the user required to change it on first login.
 */
export function SetTempPasswordDialog({
  open,
  onOpenChange,
  targetUser,
  actionLoading,
  onConfirm,
}: SetTempPasswordDialogProps) {
  const [forceChange, setForceChange] = useState(false);

  // Reset the opt-in each time the dialog opens so it always defaults OFF.
  const handleOpenChange = (openVal: boolean) => {
    if (openVal) setForceChange(false);
    onOpenChange(openVal);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            Set temporary password
          </DialogTitle>
          <DialogDescription>
            {targetUser?.user.email
              ? `Generate a temporary password for ${targetUser.user.email}.`
              : 'Generate a temporary password for this user.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            A new password will be generated and shown once so you can share it
            with the user.
          </p>

          <label
            htmlFor="force-change-checkbox"
            className="flex items-start gap-3 rounded-md border p-3 cursor-pointer"
          >
            <Checkbox
              id="force-change-checkbox"
              checked={forceChange}
              onCheckedChange={(checked) => setForceChange(checked === true)}
              disabled={actionLoading}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">Require password change on first login</span>
              <span className="block text-muted-foreground">
                Off by default. When ticked, the user must set their own password
                the first time they log in.
              </span>
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => onConfirm(forceChange)}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Setting…
              </>
            ) : (
              'Set temp password'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
